export const SUPPORTED_I18N_LANGUAGES = ['en-GB', 'es-ES', 'de-DE', 'sr-RS', 'it-IT'] as const;

export type I18nLanguage = (typeof SUPPORTED_I18N_LANGUAGES)[number];

export type I18nDictionary = {
  readonly [key: string]: I18nDictionary | string;
};

declare global {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-empty-interface -- extended via module augmentation by app resource modules
  interface I18nNamespaceRegistry {}
}

type I18nRegisteredNamespaces = I18nNamespaceRegistry;

type JoinI18nKey<Prefix extends string, Key extends string> = Prefix extends '' ? Key : `${Prefix}.${Key}`;

type LeafI18nKeys<Dictionary, Prefix extends string = ''> = Dictionary extends string
  ? never
  : {
      [Key in Extract<keyof Dictionary, string>]: Dictionary[Key] extends string
        ? JoinI18nKey<Prefix, Key>
        : Dictionary[Key] extends Record<string, unknown>
          ? LeafI18nKeys<Dictionary[Key], JoinI18nKey<Prefix, Key>>
          : never;
    }[Extract<keyof Dictionary, string>];

export type I18nNamespace = Extract<keyof I18nRegisteredNamespaces, string>;

export type I18nNamespaceDictionary<Namespace extends I18nNamespace> = I18nRegisteredNamespaces[Namespace];

export type I18nDictionaryShape<Dictionary> = Dictionary extends string
  ? string
  : {
      [Key in keyof Dictionary]: I18nDictionaryShape<Dictionary[Key]>;
    };

export type I18nScopedKey<Namespace extends I18nNamespace> = LeafI18nKeys<I18nNamespaceDictionary<Namespace>>;

export type I18nKey = {
  [Namespace in I18nNamespace]: `${Namespace}.${I18nScopedKey<Namespace>}`;
}[I18nNamespace];

export type I18nResources<Namespaces extends I18nNamespace = I18nNamespace> = {
  [language in I18nLanguage]: {
    [namespace in Namespaces]: I18nDictionaryShape<I18nNamespaceDictionary<namespace>>;
  };
};

export type I18nResourceNamespaces<Resources> =
  Resources extends Record<I18nLanguage, infer Dictionaries> ? Extract<keyof Dictionaries, I18nNamespace> : never;

export type I18nTranslateOptions = {
  defaultValue?: string;
  values?: Record<string, string | number | boolean | null | undefined>;
};

export type I18nTranslateFunction<Key extends string = I18nKey> = {
  bivarianceHack(key: Key, options?: I18nTranslateOptions): string;
}['bivarianceHack'];
